import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import {
	BooleanField,
	DateField,
	Form,
	SelectField,
	SimpleTextField,
	useModel,
	useSettings,
	useUserData,
	Utils
} from 'app/main/components';
import { DetailsSectionTable, DetailsSectionTableInputCells, ViewTableUtils } from 'app/main/components/page-layouts';

interface OrdersPromosEditDialogProps {
	/** The base model */
	baseModel: any;
	/** If true the dialog is in add mode and not into edit */
	isAdd?: boolean;
	/** Function used to refresh the related view */
	updateRefresh?: VoidFunction;
}

export function showOrdersPromosEditDialog(props: OrdersPromosEditDialogProps) {
	return openDialog({
		children: <OrdersPromosEditDialog {...props} />,
		fullWidth: true,
		maxWidth: 'lg',
		onClose: () => {}
	});
}

export default function OrdersPromosEditDialog(props: OrdersPromosEditDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('orders_promos');
	const userData = useUserData();

	const { model, sendChange, save } = useModel({
		baseModel: {
			orpm_uid: uuidv4(),
			orpm_user_created_uid: userData.user_uid,
			orpm_type: 'items',
			...props.baseModel
		},
		settings,
		isEdit: true,
		isAdd: props.isAdd
	});

	const executeSave = () => {
		save({
			onSuccess: () => {
				dispatch(Utils.alertLocalizedMsg(t, 'ALERT_UPDATE_SUCCESS', 'success'));
				dispatch(closeDialog());
				props.updateRefresh?.();
			}
		});
	};

	const itemColumns: GridColDef[] = [
		{
			field: 'orpw_item_uid',
			headerName: t('ORDERS_PROMOS_ROWS_COLUMN_ITEM'),
			minWidth: 400,
			flex: 1,
			renderCell: ({ row, value }) =>
				ViewTableUtils.renderCellWithTooltip({
					value: `[${row.item_erp_id}] ${row.item_title}`,
					to: { module: 'items', uid: value }
				}),
			editable: true,
			renderEditCell: params => (
				<DetailsSectionTableInputCells.ModuleSearchEditInputCell {...params} module="items" />
			)
		},
		{
			field: 'orpw_quantity',
			type: 'number',
			headerName: t('ORDERS_PROMOS_ROWS_COLUMN_QUANTITY'),
			minWidth: 150,
			renderCell: ViewTableUtils.renderCellWithTooltip,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.NumberEditInputCell {...params} />
		}
	];

	const discountColumns: GridColDef[] = [
		{
			field: 'orpw_discount',
			headerName: t('ORDERS_PROMOS_ROWS_COLUMN_DISCOUNT'),
			minWidth: 400,
			flex: 1,
			renderCell: ViewTableUtils.renderPercentage,
			editable: true,
			renderEditCell: params => (
				<DetailsSectionTableInputCells.NumberEditInputCell {...params} type="percentage" />
			)
		}
	];

	const noteColumns: GridColDef[] = [
		{
			field: 'orpw_note',
			headerName: t('ORDERS_PROMOS_ROWS_COLUMN_NOTE'),
			minWidth: 400,
			flex: 1,
			renderCell: ViewTableUtils.renderCellWithTooltip,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.TextEditInputCell {...params} />
		}
	];

	const rangeColumns: GridColDef[] = [
		{
			field: 'orpw_tot_value_from',
			type: 'number',
			headerName: t('ORDERS_PROMOS_ROWS_COLUMN_TOT_VALUE_FROM'),
			minWidth: 150,
			renderCell: ViewTableUtils.renderCurrency,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.CurrencyEditInputCell {...params} />
		},
		{
			field: 'orpw_tot_value_to',
			type: 'number',
			headerName: t('ORDERS_PROMOS_ROWS_COLUMN_TOT_VALUE_TO'),
			minWidth: 150,
			renderCell: ViewTableUtils.renderCurrency,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.CurrencyEditInputCell {...params} />
		}
	];

	return (
		<>
			<DialogTitle>
				<FormattedMessage
					id="ordersPromosEditDialog.title"
					defaultMessage={t(props.isAdd ? 'ORDERS_PROMOS_ADD' : 'DIALOG_TITLE_UPDATE')}
					values={{ module: `"${props.baseModel.orpm_title}"` }}
				/>
			</DialogTitle>
			<DialogContent>
				<Form id="ordersPromosEditDialog" onSubmit={executeSave}>
					<Grid container spacing={2}>
						{/* TITLE */}
						<Grid size={{ sm: 6, xs: 12 }}>
							<SimpleTextField
								name="orpm_title"
								label={t('DETAILS_ORDERS_PROMOS_FIELD_TITLE')}
								value={model.orpm_title}
								isEdit
								sendChange={sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						{/* DESCRIPTION */}
						<Grid size={{ sm: 6, xs: 12 }}>
							<SimpleTextField
								name="orpm_descriptionhtml"
								label={t('DETAILS_ORDERS_PROMOS_FIELD_DESCRIPTION')}
								value={model.orpm_descriptionhtml}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{/* DATE FROM */}
						<Grid size={{ md: 2, sm: 6, xs: 12 }}>
							<DateField
								name="orpm_d_from"
								label={t('DETAILS_ORDERS_PROMOS_FIELD_DATE_FROM')}
								value={model.orpm_d_from}
								isEdit
								sendChange={sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						{/* DATE TO */}
						<Grid size={{ md: 2, sm: 6, xs: 12 }}>
							<DateField
								name="orpm_d_to"
								label={t('DETAILS_ORDERS_PROMOS_FIELD_DATE_TO')}
								value={model.orpm_d_to}
								isEdit
								sendChange={sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						{/* TYPE */}
						<Grid size={{ md: 2, sm: 6, xs: 12 }}>
							<SelectField
								name="orpm_type"
								label={t('DETAILS_ORDERS_PROMOS_FIELD_TYPE')}
								value={model.orpm_type}
								options={[
									{ value: 'items', title: t('ORDERS_PROMOS_TYPE_ITEMS') },
									{ value: 'discounts', title: t('ORDERS_PROMOS_TYPE_DISCOUNTS') },
									{ value: 'notes', title: t('ORDERS_PROMOS_TYPE_NOTES') }
								]}
								isEdit
								sendChange={sendChange}
								afterChange={() => sendChange('orpm_rows', [])}
								disableEmptyOption
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						{/* DISABLED */}
						<Grid size={{ md: 2, sm: 6, xs: 12 }}>
							<BooleanField
								name="orpm_disabled"
								label={t('DETAILS_ORDERS_PROMOS_FIELD_DISABLED')}
								value={model.orpm_disabled}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{/* ROWS */}
						<Grid size={12}>
							<DetailsSectionTable
								name="orpm_rows"
								columns={[
									...(model.orpm_type === 'items'
										? itemColumns
										: model.orpm_type === 'discounts'
											? discountColumns
											: noteColumns),
									...rangeColumns
								]}
								idField="orpw_uid"
								rows={model.orpm_rows}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="ordersPromosEditDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
